<template>
  <CollapsibleCard
    :editable="editable"
    :editing="editing"
  >

    <template v-slot:header> LÄRLINGSPLATS </template>

    <template v-slot:info>
      {{ professionObj.text }},
      {{ applicationEmployee ? applicationEmployee.firstName + ' ' + applicationEmployee.lastName : '' }},
      {{ get(applicationWorkplace,'name','') }}, {{ get(applicationWorkplace, 'postalCity', '') }},
      {{ getEmploymentTypeNameById(Number(selectedApplication.employmentTypeId)) }}
    </template>

    <template v-slot:detailsinfo></template>

    <template v-slot:editinfo>
      Yrke, handledare, arbetsplats, avdelning, anställningsform och lärlingstidens startdatum.
    </template>

    <template v-slot:view>
      <ApplicationProfessionView
        :professionObj="professionObj"
        :application="application"
        :professionOrientationsArr="professionOrientationsArr"
        :professionOrientationsSelected="professionOrientationsSelected"
        @saved="$emit('saved')"
      />
    </template>

    <template v-slot:edit>
      <ApplicationProfessionEdit
        :professions="professions"
        :application="application"
        :pickerDate="pickerDate"
        :defaultDate="defaultDate"
        :employmentTypes="employmentTypes"
        :professionOrientationsArr="professionOrientationsArr"
        @saved="saved"
        @change-date="pickerDate = $event"
        @update-profession-orientations = "professionOrientationsArr = $event"
        ref="edit"
      />
    </template>

    <template v-slot:cancel>
      <v-btn
        color="secondary"
        @click="cancelProfession"
      >
        <v-icon>$sl-cancel</v-icon>Avbryt
      </v-btn>
    </template>

    <template v-slot:save>
      <v-btn
        data-cy="save-application-profession"
        @click="$refs.edit.saveProfession()"
      >
        <v-icon>$sl-check</v-icon>Spara
      </v-btn>
    </template>

  </CollapsibleCard>
</template>

<script>
import find from 'lodash/find'
import get from 'lodash/get'
import filter from 'lodash/filter'
import cloneDeep from 'lodash/cloneDeep'
import MonthPicker from '@/components/common/MonthPicker/MonthPicker.vue'
import CollapsibleCard from '@/components/common/CollapsibleCard'
import {InstructorAbilities, ApplicationProfession} from '@/components/mixins'
import ApplicationProfessionView from './ApplicationProfessionView.vue'
import ApplicationProfessionEdit from './ApplicationProfessionEdit.vue'

// @group Application
export default {
  name: 'ApplicationProfession',

  props: {
    // Should fields be editable
    editMode: {
      // `true` / `false`
      type: Boolean,
      // The default value is: `false`
      default: false
    },
    editable: {
      type: Boolean,
      default: false
    }
  },

  mixins: [InstructorAbilities, ApplicationProfession],

  components: {
    MonthPicker,
    CollapsibleCard,
    ApplicationProfessionView,
    ApplicationProfessionEdit
  },

  data: () => ({
    date: new Date().toISOString()
      .substr(0, 10),
    editing: false,
    isLoading: false,
    initProfessionId: null,
    initApplication: {},
    application: {},
    professionObj: {},
    professions: [],
    professionOrientationsArr: [],
    professionOrientationsSelected: '',
    employmentTypes: [],
    employeeType: {},
    pickerDate: null,
    defaultDate: null,
    workplaces: []
  }),

  watch: {
    // @vuese
    //- Make sure any changes in vuex state is reflected in this.state
    selectedApplication: {
      handler() {
        this.setUpInitdate()
        this.application = cloneDeep(this.selectedApplication)
        this.setOrientations()
        if (this.selectedApplication && this.selectedApplication.id) {
          this.getProfessions()
          this.getEmploymentTypes()
        }
      },
      deep: true
    },

    pickerDate(val) {
      if (this.selectedApplication) {
        this.selectedApplication.employmentDate = this.pickerDate
        this.defaultDate = this.pickerDate
      }
    },
    deep: true
  },

  created() {
    this.setUpInitdate()
    this.getProfessions()
    this.getEmploymentTypes()
    this.initApp()
  },

  computed: {
    filteredDepartments() {
      let ret
      if (this.application.workplaceId && this.applicationDepartments)
        ret = this.applicationDepartments.filter(dep => dep.workplaceIds.find(u => u == this.application.workplaceId))
      return ret
    }
  },

  methods: {
    get,

    saved() {
      this.$emit('saved')
      this.editing = false
    },

    setUpInitdate() {
      if (this.selectedApplication.employmentDate) {
        this.pickerDate = this.$options.filters.date(this.selectedApplication.employmentDate)
        this.defaultDate = this.$options.filters.date(this.selectedApplication.employmentDate)
      } else {
        this.pickerDate = null
        this.defaultDate = null
      }
    },

    initApp() {
      this.application = cloneDeep(this.selectedApplication)
      if (
        this.selectedApplication.professionOrientations &&
        this.selectedApplication.professionOrientations.length > 0
      ) {
        this.selectedOrientationIds = this.selectedApplication.professionOrientations.map(
          o => o.professionMachineOrientationId
        )
      } else {
        const defaultOrientations = filter(this.orientations, ['isDefaultIncluded', true])
        this.selectedOrientationIds = defaultOrientations.map(o => o.id)
      }
    },

    setOrientations() {
      const jobs = get(this, 'application.professionOrientations', [])

      if (jobs.length > 0) {
        this.professionOrientationsArr = this.application.professionOrientations.map(
          o => o.professionMachineOrientationId
        )

        //set the professionOrientationsSelected text
        this.professionOrientationsSelected = ''
        this.application.professionOrientations.forEach(element => {
          this.professionOrientationsSelected += ', ' + element.professionMachineOrientation.machine.name
        })
        if (this.professionOrientationsSelected.length > 0) {
          this.professionOrientationsSelected = this.professionOrientationsSelected.substring(
            1,
            this.professionOrientationsSelected.length
          )
        }
      } else {
        this.professionOrientationsSelected = ''
        const defaultOrientations = filter(this.orientations, ['isDefaultIncluded', true])
        this.professionOrientationsArr = defaultOrientations.map(o => o.id)
      }
    },

    // @vuese
    //- Used to get all the Profession list
    getProfessions() {
      this.professions = filter(this.basicProfessions, ['isApplicationProfession', true]).map(o => {
        return {id: o.id, text: o.name}
      })

      let prof = find(this.professions, {
        id: this.selectedApplication.professionId
      })
      this.professionObj = prof || {}
    },

    // @vuese
    //- Used to get all the employements list
    getEmploymentTypes() {
      this.employmentTypes = this.basicEmploymentTypes.map(o => {
        return {id: o.id, text: o.name}
      })

      let types = find(this.professions, {
        id: this.selectedApplication.employmentTypeId
      })
      types ? (this.employeeType = types) : (this.employeeType = {})
    },

    cancelProfession() {
      this.setUpInitdate()
      this.getProfessions()
      this.getEmploymentTypes()
      this.initApp()
      // this.getProfessions()
      this.editing = false
    },

    changeProfession() {
      this.initProfessionId = this.selectedApplication.professionId
      this.initEmploymentTypeId = this.selectedApplication.employmentTypeId
      this.initEmploymentDate = this.selectedApplication.employmentDate
      this.editing = true
    }
  }
}
</script>

<style>
</style>
